import { addResponseStatusToSpan } from '../../../utils/httpTracing';
import type { Schema } from 'effect';
import { Effect } from 'effect';

import { createBackendApiError } from '../../../data/error/backendApi/_internal/createBackendApiError';
import { BackendApi } from '../../../service/api/BackendApi';
import { ApiModule } from '../../../service/logger/ApiModule';
import { logApiOperation } from '../../../utils/logger/apiLogger';
import type { Paths } from '@vs-shop/schemas/openapi/paths';
import { HydraCollectionResponse } from '@vs-shop/schemas/openapi/response';
import { schemaBodyJsonClean } from '@vs-shop/schemas/transform/schemaBodyJsonClean';
import * as HttpClientResponse from 'effect/unstable/http/HttpClientResponse';

const ENDPOINT = '/api/v2/shop/orders';
const HTTP_METHOD = 'get';

type QueryOf<Op> = Op extends { parameters: { query?: infer Q } } ? Q : never;

type ListOrdersStates = NonNullable<
  NonNullable<QueryOf<Paths[typeof ENDPOINT][typeof HTTP_METHOD]>>['state[]']
>;

export type MakeListOrdersForUserDeps<OIndex extends Schema.Top> = {
  readonly orderIndex: OIndex;
};

export const makeListOrdersForUser = <OIndex extends Schema.Top>(
  deps: MakeListOrdersForUserDeps<OIndex>
) => {
  const ListOrdersForUserError = createBackendApiError({
    domain: 'Order',
    endpoint: 'ListForUser',
    path: ENDPOINT,
    identifierField: 'orderState',
  } as const);

  const getOrdersForUserFetch = (
    orderState: ListOrdersStates,
    page: number | null,
    itemsPerPage: number | null
  ) =>
    Effect.gen(function* () {
      const apiClient = yield* BackendApi;
      return yield* apiClient
        .path(ENDPOINT)
        .method(HTTP_METHOD)
        .accept('application/ld+json')
        .fetch({
          query: {
            'state[]': orderState,
            ...(page !== null ? { page } : {}),
            ...(itemsPerPage !== null ? { itemsPerPage } : {}),
          },
        });
    }).pipe(
      Effect.withSpan(`http.${HTTP_METHOD} ${ENDPOINT}`, {
        kind: 'client',
        attributes: {
          'http.request.method': HTTP_METHOD,
          'userOrders.orderState': orderState.join(','),
          ...(page !== null ? { 'userOrders.page': page } : {}),
          ...(itemsPerPage !== null ? { 'userOrders.itemsPerPage': itemsPerPage } : {}),
        },
      })
    );

  const listOrdersForUser = (
    orderState: ListOrdersStates,
    page: number | null,
    itemsPerPage: number | null
  ) =>
    getOrdersForUserFetch(orderState, page, itemsPerPage).pipe(
      Effect.map((response) => response.response),
      Effect.tap(addResponseStatusToSpan),
      Effect.withSpan('ValidateResponse'),
      Effect.flatMap(
        HttpClientResponse.matchStatus({
          '2xx': (response) =>
            schemaBodyJsonClean(HydraCollectionResponse(deps.orderIndex))(response),
          404: (response) =>
            Effect.fail(new ListOrdersForUserError.NotFoundError({ orderState, response })),
          orElse: (response) =>
            Effect.fail(new ListOrdersForUserError.ApiRequestError({ orderState, response })),
        })
      ),
      Effect.tap((result) =>
        logApiOperation(
          ApiModule.OrderList,
          'Debug',
          `Successfully resolved ${String(result['hydra:member'].length)} orders for states: ${orderState.join(',')}`
        )
      ),
      Effect.withSpan('api.order.list', {
        kind: 'internal',
        attributes: {
          'userOrders.orderState': orderState.join(','),
          ...(page !== null ? { 'userOrders.page': page } : {}),
          ...(itemsPerPage !== null ? { 'userOrders.itemsPerPage': itemsPerPage } : {}),
        },
      })
    );

  return { listOrdersForUser, ListOrdersForUserError };
};
